import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText, X, Send, Sparkles, BadgeCheck } from "lucide-react";
import { API } from "../lib/constants";

const GREETING = {
  role: "assistant",
  content:
    "Olá! Eu sou a Estrela, assistente da Barbearia das Estrelas e Prime Capilar Clinic. Posso tirar dúvidas sobre barbearia, prótese capilar, clube e unidades — ou te ajudar a encontrar a solução capilar ideal. Como posso ajudar?",
};

const SUGGESTIONS = [
  "Como funciona a prótese capilar?",
  "Quais são os planos do clube?",
  "Quero uma avaliação capilar",
  "Onde ficam as unidades?",
];

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("bde_chat_session") || null);
  const [leadSaved, setLeadSaved] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  if (location.pathname.startsWith("/admin")) return null;

  async function send(text) {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content, page: location.pathname }),
      });
      if (!res.ok) {
        let msg = "Não consegui responder agora. Fale conosco no WhatsApp: https://wa.me/5582991863607";
        if (res.status === 429) msg = "Opa, você enviou muitas mensagens em pouco tempo. Aguarde alguns segundos e tente novamente. 🙏";
        else if (res.status === 422) msg = "Sua mensagem ficou muito longa. Tente resumir em até 1000 caracteres. 🙂";
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: msg };
          return copy;
        });
        setSending(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.session_id) {
            setSessionId(payload.session_id);
            localStorage.setItem("bde_chat_session", payload.session_id);
          }
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.delta,
              };
              return copy;
            });
          }
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
          if (payload.lead_saved) {
            setLeadSaved(true);
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Tive um problema de conexão. Fale conosco no WhatsApp: https://wa.me/5582991863607",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher-button"
        aria-label="Abrir assistente virtual"
        className="fixed z-50 bottom-5 left-5 flex items-center gap-2.5 bg-gold hover:bg-gold-hover text-black font-heading font-600 tracking-wide pl-3.5 pr-5 py-3.5 rounded-full shadow-2xl shadow-black/50 transition-transform hover:scale-[1.03]"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquareText className="w-6 h-6" />}
        <span className="text-sm hidden sm:inline">{open ? "FECHAR" : "TIRAR DÚVIDAS"}</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-50 bottom-24 left-5 right-5 sm:right-auto sm:w-[380px] h-[72vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden border border-gold/25 bg-[#0b0b0b] shadow-2xl shadow-black/70 animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-gold/15 to-transparent border-b border-white/10">
            <div className="w-9 h-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5 text-gold" />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-600 text-white text-sm">Estrela · Assistente</p>
              <p className="text-[11px] text-gold">Online agora</p>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto text-zinc-400 hover:text-white" aria-label="Fechar chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-gold text-black rounded-br-sm"
                      : "bg-white/[0.06] text-zinc-100 border border-white/10 rounded-bl-sm"
                  }`}
                >
                  {m.content || (sending && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}

            {messages.length <= 1 && (
              <div className="pt-1 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                    className="text-xs text-gold border border-gold/40 hover:bg-gold/10 rounded-full px-3 py-1.5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {leadSaved && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366]/12 border-t border-[#25D366]/30 text-[#25D366]" data-testid="chat-lead-saved">
              <BadgeCheck className="w-4 h-4 shrink-0" />
              <span className="text-xs font-heading font-600">Contato registrado! Nossa equipe vai te chamar no WhatsApp.</span>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 p-3 border-t border-white/10 bg-[#0d0d0d]"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={1000}
              placeholder="Escreva sua mensagem…"
              data-testid="chat-input"
              className="flex-1 bg-white/[0.05] border border-white/12 focus:border-gold/60 outline-none rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send-button"
              className="w-10 h-10 shrink-0 rounded-full bg-gold hover:bg-gold-hover text-black flex items-center justify-center disabled:opacity-40 transition-colors"
              aria-label="Enviar"
            >
              <Send className="w-4.5 h-4.5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
